
import React from "react";
import { Phone, Calendar, MessageSquare, Star, Users, CheckCircle, Plane, Home, LogOut } from "lucide-react";
import ScrollReveal from "@/components/ui/scroll-reveal";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";

const TimelineConnector = ({ direction = "horizontal" }: { direction?: "horizontal" | "vertical" }) => {
  if (direction === "vertical") {
    return (
      <div className="flex flex-col items-center justify-center h-full w-full">
        <svg 
          className="h-full w-4"
          viewBox="0 0 20 100"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="flowGradientVertical" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
              <stop offset="50%" stopColor="hsl(var(--primary))" stopOpacity="0.7" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
            </linearGradient>
          </defs>
          <path 
            d="M10,0 L10,100" 
            stroke="url(#flowGradientVertical)"
            strokeWidth="2"
            strokeDasharray="6 4"
            fill="none"
            className="animate-flow-down"
          />
        </svg>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center w-full h-full">
      <svg 
        className="w-full h-4"
        viewBox="0 0 100 20"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="flowGradientHorizontal" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
            <stop offset="50%" stopColor="hsl(var(--primary))" stopOpacity="0.7" />
            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
          </linearGradient>
        </defs>
        <path 
          d="M0,10 L100,10" 
          stroke="url(#flowGradientHorizontal)"
          strokeWidth="2"
          strokeDasharray="6 4"
          fill="none"
          className="animate-flow-right"
        />
        {/* Animated dot */}
        <circle r="3" fill="hsl(var(--primary))" opacity="0.8">
          <animateMotion dur="2s" repeatCount="indefinite" path="M0,10 L100,10" />
        </circle>
      </svg>
    </div>
  );
};

const FeatureCard = ({ 
  icon, 
  title, 
  description, 
  index,
  journeyPhase,
  journeyIcon: JourneyIcon
}: { 
  icon: React.ReactNode, 
  title: string, 
  description: string, 
  index: number,
  journeyPhase: string,
  journeyIcon: React.ElementType
}) => {
  return (
    <ScrollReveal 
      delay={index * 150} 
      direction="fade" 
      duration={800}
      className="h-full flex"
    >
      <div className="relative bg-white dark:bg-card p-6 rounded-xl shadow-sm border border-gray-100 dark:border-border h-full hover:shadow-md transition-all duration-300 hover:-translate-y-1 flex flex-col flex-1">
        {/* Journey phase indicator - upper right with subtle animation */}
        <div className="absolute -top-2.5 right-3 flex items-center gap-1 px-2 py-0.5 bg-brand-50 dark:bg-brand-900/40 border border-brand-200 dark:border-brand-700 rounded-full animate-fade-in hover:scale-105 transition-transform duration-200">
          <JourneyIcon size={10} className="text-brand-600 dark:text-brand-400" />
          <span className="text-[10px] font-medium text-brand-700 dark:text-brand-300">{journeyPhase}</span>
        </div>
        
        <div className="w-12 h-12 bg-brand-100 dark:bg-brand-900/30 rounded-lg text-brand-600 dark:text-brand-400 flex items-center justify-center mb-4 mt-2 shrink-0">
          {icon}
        </div>
        <h3 className="text-xl font-semibold mb-3">{title}</h3>
        <p className="text-gray-600 dark:text-muted-foreground flex-grow">{description}</p>
      </div>
    </ScrollReveal>
  );
};

const Features = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: <Phone size={24} />,
      title: t("features.callHandling"),
      description: t("features.featureCard1.description"),
      journeyPhase: "Booking",
      journeyIcon: Calendar
    },
    {
      icon: <Calendar size={24} />,
      title: t("features.preStay"),
      description: t("features.featureCard2.description"),
      journeyPhase: "Pre-Stay",
      journeyIcon: Plane
    },
    {
      icon: <MessageSquare size={24} />,
      title: t("features.inStay"),
      description: t("features.featureCard3.description"),
      journeyPhase: "In-Stay",
      journeyIcon: Home
    },
    {
      icon: <Star size={24} />,
      title: t("features.postStay"),
      description: t("features.featureCard4.description"),
      journeyPhase: "Post-Stay",
      journeyIcon: LogOut
    },
    {
      icon: <Users size={24} />,
      title: t("features.multilingual"),
      description: t("features.featureCard2.description"),
      journeyPhase: "All Phases",
      journeyIcon: Users
    },
    {
      icon: <CheckCircle size={24} />,
      title: t("features.integration"),
      description: t("features.featureCard3.description"),
      journeyPhase: "All Phases",
      journeyIcon: CheckCircle
    },
  ];

  return (
    <section className="section-padding bg-gray-50 dark:bg-background overflow-x-hidden">
      <div className="container mx-auto px-4">
        <ScrollReveal direction="up" duration={700}>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t("features.title")}</h2>
            <p className="text-lg text-gray-600 dark:text-muted-foreground">
              {t("features.subtitle")}
            </p>
          </div>
        </ScrollReveal>

        {/* Timeline container with journey labels */}
        <div className="relative max-w-6xl mx-auto">
          {/* Journey start label - desktop */}
          <div className="hidden lg:flex items-center gap-2 absolute -top-8 left-0">
            <div className="w-3 h-3 rounded-full bg-primary animate-pulse" />
            <span className="text-sm text-primary font-medium">
              Booking
            </span>
          </div>
          
          {/* Journey end label - desktop */}
          <div className="hidden lg:flex items-center gap-2 absolute -bottom-8 right-0">
            <span className="text-sm text-primary font-medium">
              Checkout & Beyond
            </span>
            <div className="w-3 h-3 rounded-full bg-primary animate-pulse" />
          </div>

          {/* Grid with feature cards and connectors */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-8 lg:gap-y-12 gap-x-6 auto-rows-fr items-start pt-4">
            {features.map((feature, index) => (
              <div key={index} className="relative h-full flex flex-col">
                <FeatureCard
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  index={index}
                  journeyPhase={feature.journeyPhase}
                  journeyIcon={feature.journeyIcon}
                />
                
                {/* Horizontal connector - desktop (between cards in same row) */}
                {(index % 3 !== 2) && index < 5 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-8 -translate-y-1/2 z-0">
                    <TimelineConnector direction="horizontal" />
                  </div>
                )}
                
                {/* Vertical connector between rows - desktop (after card 3) */}
                {index === 2 && (
                  <div className="hidden lg:block absolute -bottom-6 left-1/2 -translate-x-1/2 w-8 h-6 z-0">
                    <TimelineConnector direction="vertical" />
                  </div>
                )}

                {/* Vertical connector - mobile/tablet (between all cards except last) */}
                {index < 5 && (
                  <div className="lg:hidden absolute left-8 -bottom-4 w-4 h-4 z-0">
                    <div className="w-0.5 h-full bg-gradient-to-b from-primary/50 to-primary/30 mx-auto" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
